import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, PhoneCall, MapPin } from "lucide-react";
import Button from "@/components/base/Button";
import { serviceCategories } from "@/data/services";
import { site } from "@/data/site";

const navLinks = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Doctors", to: "/doctors" },
  { label: "Services", to: "/services", mega: true },
  { label: "Gallery", to: "/gallery" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

import { img } from "@/data/images";

function Logo({ dark }: { dark: boolean }) {
  return (
    <Link to="/" className="flex items-center gap-3" aria-label="AV Clinic home">
      <img
        src={img.logo}
        alt="AV Clinic"
        className={`h-10 w-auto transition-all duration-300 ${dark ? "brightness-200 contrast-125 drop-shadow" : ""}`}
        style={{ maxHeight: 40 }}
      />
      <span className="leading-tight">
        <span className={`block font-heading text-lg font-semibold ${dark ? "text-background-50" : "text-primary-950"}`}>
          AV Clinic
        </span>
        <span className={`block text-[11px] tracking-wide ${dark ? "text-primary-100/80" : "text-foreground-500"}`}>
          Skin · Urology · Andrology
        </span>
      </span>
    </Link>
  );
}

export default function Header({ onOpenMenu }: { onOpenMenu: () => void }) {
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMegaOpen(false);
  }, [location.pathname]);

  const solid = scrolled || !isHome;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        solid ? "bg-background-50/95 backdrop-blur border-b border-background-200 shadow-soft" : "bg-transparent"
      }`}
    >
      {/* top contact strip */}
      <div
        className={`hidden lg:block border-b transition-colors duration-300 ${
          solid ? "border-background-200" : "border-background-50/20"
        }`}
      >
        <div className="mx-auto max-w-container px-4 md:px-6 py-2 flex items-center justify-between">
          <div className={`flex items-center gap-5 text-xs ${solid ? "text-foreground-600" : "text-primary-100/90"}`}>
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-accent-500" />
              {site.shortAddress}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-time-line" /> Mon–Sat · 10:00 AM – 8:30 PM
            </span>
          </div>
          <div className={`flex items-center gap-4 text-xs ${solid ? "text-foreground-600" : "text-primary-100/90"}`}>
            <a href={`mailto:${site.email}`} className="hover:text-accent-500 transition-colors whitespace-nowrap">
              {site.email}
            </a>
            <a
              href={`tel:${site.phones[0].number}`}
              className={`inline-flex items-center gap-1.5 font-semibold ${solid ? "text-primary-800" : "text-accent-300"} hover:text-secondary-500 transition-colors whitespace-nowrap`}
            >
              <PhoneCall className="w-3.5 h-3.5" /> {site.phones[0].display}
            </a>
          </div>
        </div>
      </div>

      {/* main nav */}
      <div className="mx-auto max-w-container px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Logo dark={!solid} />
          <nav className="hidden lg:flex items-center gap-1" aria-label="Main navigation">
            {navLinks.map((link) =>
              link.mega ? (
                <div
                  key={link.to}
                  className="relative"
                  onMouseEnter={() => setMegaOpen(true)}
                  onMouseLeave={() => setMegaOpen(false)}
                >
                  <Link
                    to={link.to}
                    className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-semibold rounded-md transition-colors whitespace-nowrap ${
                      solid ? "text-primary-900 hover:text-secondary-600" : "text-background-50 hover:text-accent-300"
                    }`}
                  >
                    Services <ChevronDown className="w-4 h-4" />
                  </Link>
                  {megaOpen && (
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3">
                      <div className="w-[560px] rounded-lg border border-background-200 bg-background-50 shadow-soft p-5">
                        <div className="grid grid-cols-2 gap-2">
                          {serviceCategories.map((cat) => (
                            <Link
                              key={cat.slug}
                              to={`/services/${cat.slug}`}
                              className="group rounded-md p-3 hover:bg-secondary-50 transition-colors"
                            >
                              <span className="text-sm font-semibold text-primary-900 group-hover:text-secondary-700">
                                {cat.name}
                              </span>
                              <span className="block text-xs text-foreground-500 mt-0.5">
                                {cat.short}
                              </span>
                            </Link>
                          ))}
                        </div>
                        <Link
                          to="/services"
                          className="mt-3 block rounded-md bg-primary-50 px-4 py-3 text-sm font-semibold text-primary-800 text-center hover:bg-primary-100 transition-colors"
                        >
                          View full services directory
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors whitespace-nowrap ${
                    solid ? "text-primary-900 hover:text-secondary-600" : "text-background-50 hover:text-accent-300"
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}
          </nav>
          <div className="flex items-center gap-3">
            <div className="hidden md:block">
              <Button to="/appointment" size="sm" variant="accent">
                Book Appointment
              </Button>
            </div>
            <button
              type="button"
              onClick={onOpenMenu}
              aria-label="Open menu"
              className={`lg:hidden w-11 h-11 flex items-center justify-center rounded-md cursor-pointer ${
                solid ? "text-primary-900 bg-primary-50" : "text-background-50 bg-primary-500/30"
              }`}
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}